import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { UserType } from '@shared/enums/user-type';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { LocalStorageService } from 'app/storage/local-storage-service';
import { map, Observable, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ChangePasswordDialogComponent } from '@shared/components/change-password-dialog/change-password-dialog.component';
import { ExternalChangePasswordDialogComponent } from '@shared/components/external-change-password-dialog/external-change-password-dialog.component';

@Injectable({ providedIn: 'root' })
export class UserAuthorizationGuard implements CanActivate {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly localStorage = inject(LocalStorageService);
  private readonly dialog = inject(MatDialog);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.oidcSecurityService.checkAuth().pipe(
      take(1),
      map(({ isAuthenticated }) => {
        //console.log('Authentication check result:', { isAuthenticated });
        if (!isAuthenticated) {
          this.localStorage.saveRedirectUrl(state.url);
          this.oidcSecurityService.authorize();
          return false;
        }

        const allowedTypes: UserType[] = route.data['allowedUserTypes'];

        //console.log('Allowed user types for this route:', allowedTypes);
        const user = this.localStorage.getUserProfile();

        //console.log('Current user profile:', user);

        if (!user || !allowedTypes.includes(user.userType as UserType)) {
          return this.router.createUrlTree(['/unauthorized']);
        }

        if (user.requireNewPassword) {
          if (user.userType === UserType.INTERNAL_USER) {
            const isOpen = this.dialog.openDialogs.some(
              (d) => d.componentInstance instanceof ChangePasswordDialogComponent
            );
            if (!isOpen) {
              this.dialog.open(ChangePasswordDialogComponent, {
                width: '450px',
                disableClose: true,
                closeOnNavigation: false,
                data: { requireNewPassword: true }
              });
            }
          } else if (user.userType === UserType.EXTERNAL_USER) {
            const isOpen = this.dialog.openDialogs.some(
              (d) => d.componentInstance instanceof ExternalChangePasswordDialogComponent
            );
            if (!isOpen) {
              this.dialog.open(ExternalChangePasswordDialogComponent, {
                width: '850px',
                disableClose: true,
                closeOnNavigation: false,
                data: { requireNewPassword: true }
              });
            }
          }
        }

        return true;
      }),
    );
  }
}
